import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import { fetchInvoices, buildPendingFeatureInvoiceMap } from '@/store/slices/wallet';
import { fetchConfiguratorConfig } from '@/store/slices/configurator-config';

import { artBundleText, DEFAULT_CURRENCY, DEFAULT_LANG, enabledLadder, tierLabelFor } from '../../../../utils/catalog';
import { currencySymbol, formatAmountWithCode } from '../../../../utils/money';
import type { ImportantNotice } from './useImportantNotices';

/**
 * Premium Spotlight notices for the active brand's PENDING feature-upgrade invoices — a feature
 * the owner began activating / upgrading but never finished paying. Each becomes a premium
 * "Finish your upgrade" card (feature icon + name + tier + price + a Finish-payment CTA).
 *
 * Self-contained and API-driven, like {@link useImportantNotices}: it fetches the owner invoices
 * + the `/configurator` catalog itself, matches the pending feature invoices to the active brand
 * (`buildPendingFeatureInvoiceMap`, brand-scoped), and resolves each card's display data — the
 * localized feature name + artwork + tier label from the catalog, the amount from the invoice.
 * Returns `[]` when nothing is pending (so no premium card renders).
 */
export const usePendingUpgradeNotices = (): ImportantNotice[] => {
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();
  const lang = i18n.language || DEFAULT_LANG;

  const invoices = useAppSelector((s) => s.ownerWallet.invoices);
  const invoicesLoaded = useAppSelector((s) => s.ownerWallet.invoicesLoaded);
  const invoicesLoading = useAppSelector((s) => s.ownerWallet.invoicesLoading);
  const catalog = useAppSelector((s) => s.configuratorConfig.gamificationItems);
  const catalogLoaded = useAppSelector((s) => s.configuratorConfig.loaded);
  const catalogLoading = useAppSelector((s) => s.configuratorConfig.loading);
  // The brand the page renders (the lagged switcher selection) scopes the invoices to this brand.
  const activeBrandId = useAppSelector((s) => s.casinoInstance.displayBrand?.id);

  // One-shot fetch of the invoices + catalog on mount, guarded so it never duplicates an
  // in-flight/finished load from elsewhere. The `requested` ref is what guarantees at-most-once:
  // the catalog slice resets loaded=false on a rejected fetch (a bare `!loaded && !loading` effect
  // would re-fire forever), while the invoices slice fails CLOSED (invoicesLoaded=true on reject).
  const requested = React.useRef(false);
  React.useEffect(() => {
    if (requested.current) return;
    requested.current = true;
    if (!invoicesLoaded && !invoicesLoading) dispatch(fetchInvoices());
    if (!catalogLoaded && !catalogLoading) dispatch(fetchConfiguratorConfig());
  }, [dispatch, invoicesLoaded, invoicesLoading, catalogLoaded, catalogLoading]);

  return React.useMemo(() => {
    const pending = buildPendingFeatureInvoiceMap(invoices, activeBrandId);
    if (pending.size === 0) return [];

    // Newest-first so the freshest unpaid upgrade leads the Spotlight.
    const sorted = [...pending.values()].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
    const notices: ImportantNotice[] = [];
    for (const invoice of sorted) {
      const itemId = invoice.invoice_data?.campaign_item?.id;
      if (typeof itemId !== 'number') continue;

      const catalogItem = catalog.find((entry) => entry.id === itemId);
      const bundle = catalogItem?.art_bundle?.[0];
      // Prefer the localized catalog name; fall back to the invoice's, then the raw catalog name.
      const name =
        (catalogItem ? artBundleText(bundle, lang) : undefined) ??
        invoice.invoice_data?.campaign_item?.name ??
        catalogItem?.name ??
        '';
      const image = bundle?.logo ?? '';

      // Tier label from the catalog ladder (strips the feature prefix → "Pro"); blank until the
      // catalog resolves (the card then leads with the feature name + price alone).
      const tierId = invoice.invoice_data?.new_item?.tier_id;
      let tierName = '';
      if (catalogItem && tierId !== undefined) {
        const tier = enabledLadder(catalogItem).find((entry) => entry.id === tierId);
        if (tier) tierName = tierLabelFor(tier, catalogItem.name, lang);
      }

      const currency = invoice.currency || DEFAULT_CURRENCY;
      const priceText = `${currencySymbol(currency)}${formatAmountWithCode(Number(invoice.price) || 0, currency)}`;

      notices.push({
        kind: 'feature-payment',
        // Unused for this kind — the premium card owns its own chrome (see ImportantNow).
        tone: 'action',
        payment: { itemId, invoiceId: invoice.id, name, image, tierName, priceText },
      });
    }
    return notices;
  }, [invoices, activeBrandId, catalog, lang]);
};
